import { Component, computed, input, InputSignal, Signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { KYC_KYB_URLS } from '@app/models/kyc-kyb/types';
import { PersonaScope } from '@app/modules/payroll/components/kyc-kyb/types';
import { AppDatePipe } from '@app/pipes';
import { PlatformModule } from '@app/platform.module';
import { KYCKYBService } from '@app/services/kyc-kyb/kyc-kyb.service';
import { TranslateModule } from '@ngx-translate/core';
import { SpecialInformationBarService } from '../../../special-information-bar.service';
import { BaseInformationBar } from '../base-information-type.component';

/**
 * Banner shown when KYC or KYB is due for re-verification
 * For KYB: shown to admins when business verification is expiring
 * For KYC: shown to users when identity verification is expiring or payroll access is blocked
 */
@Component({
    selector: 'special-info-kyc-kyb-re-verification',
    standalone: true,
    imports: [PlatformModule, TranslateModule, MatIconModule, RouterLink, AppDatePipe],
    templateUrl: './kyc-kyb-re-verification.template.html',
})
export class SpecialInfoKYCKYBReVerification extends BaseInformationBar {
    scope: InputSignal<PersonaScope> = input.required();
    shouldDisplay: Signal<boolean> = computed(() => {
        const { hasPayroll, isAdmin, KYCKYB, hasKYCExpired, hasKYBExpired, currentURL, isEmployeeOnboarding } =
            this.displayConditions() || {};

        // Don't show if employee is onboarding
        if (isEmployeeOnboarding) {
            return false;
        }

        // Don't show if no payroll access
        if (!hasPayroll) {
            return false;
        }

        const { hasVerifiedPersonaIdentity } = KYCKYB || {};

        // Don't show if already on the verification page for this scope
        if (KYC_KYB_URLS[this.scope()] === currentURL) {
            return false;
        }

        // KYC
        if (this.scope() === 'KYC') {
            if ((hasPayroll || hasKYCExpired) && !hasVerifiedPersonaIdentity && hasKYBExpired) {
                return true;
            }

            return false;
        }

        // KYB
        if (this.scope() === 'KYB') {
            // Don't show if the inquiry is in not in a state that can start reverification
            if (KYCKYB?.kybStatus == 'in_review' || KYCKYB?.kybStatus == 'in_progress') {
                return false;
            }

            // Show if company persona is expiring AND user is admin
            if (hasPayroll && !!isAdmin && hasKYBExpired) {
                return true;
            }

            return false;
        }

        return false;
    });

    get linkToPreRedirect(): string {
        return KYC_KYB_URLS[this.scope()];
    }

    deadlineDate: Signal<Date> = computed(() => {
        return this.kycKybService.getReVerificationDeadlineDate();
    });

    constructor(
        private kycKybService: KYCKYBService,
        specialInformationBarService: SpecialInformationBarService
    ) {
        super(specialInformationBarService);
    }
}
